import type { TFunction } from 'i18next';

import type { TIntegrationDataType } from '@feathr/blackbox';
import { EIntegrationSyncRule } from '@feathr/blackbox';
import type { IIntegrationUIMappingField, IIntegrationUITab } from '@feathr/components';

/** The Mappings, Activities and Settings tabs every CRM integration page has. */
export function getCrmTabs(t: TFunction): IIntegrationUITab[] {
  return [
    { key: 'mappings', label: t('Mappings'), path: 'mapping' },
    { key: 'activities', label: t('Activities'), path: 'activities' },
    { key: 'settings', label: t('Settings'), path: 'settings' },
  ];
}

export interface ICrmMappingFieldsOptions<TKey extends string> {
  keys: readonly TKey[];
  dataTypes: Readonly<Record<TKey, TIntegrationDataType>>;
  getLabel: (key: TKey) => string;
  /** Fields every integration maps. They can't be edited or removed. */
  requiredKeys: ReadonlySet<string>;
  /** Why syncing to the CRM is not available, shown on the disabled directions. */
  upsyncUnavailable: string;
}

/** Every CRM contact field. Each syncs only from the CRM to Feathr for now. */
export function getCrmMappingFields<TKey extends string>({
  dataTypes,
  getLabel,
  keys,
  requiredKeys,
  upsyncUnavailable,
}: ICrmMappingFieldsOptions<TKey>): IIntegrationUIMappingField[] {
  return keys.map((key) => ({
    allowedSyncRules: [EIntegrationSyncRule.downSync],
    dataType: dataTypes[key],
    disabledSyncRules: [
      { rule: EIntegrationSyncRule.upSync, reason: upsyncUnavailable },
      { rule: EIntegrationSyncRule.bothWays, reason: upsyncUnavailable },
    ],
    key,
    label: getLabel(key),
    locked: requiredKeys.has(key),
    required: requiredKeys.has(key),
  }));
}
